import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useClerk } from '@clerk/react';
import {
  Container,
  Title,
  Card,
  Text,
  Badge,
  Stack,
  Group,
  Loader,
  Alert,
  Button,
  Avatar,
  Divider,
  SimpleGrid,
  Paper,
  Box,
  ThemeIcon,
} from '@mantine/core';
import {
  IconExclamationCircle,
  IconMapPin,
  IconUser,
  IconStar,
  IconShoppingBag,
  IconMessage,
  IconSparkles,
  IconArrowLeft,
  IconHeart,
  IconEye,
  IconVideo,
  IconAlbum,
  IconHeartStar,
} from '@tabler/icons-react';
import { useApi, useLiveQuery, useApiClient, useSession, mediaUrl } from '../api';
import { formatCents, primaryImagePath } from '../components/products/productDisplay';
import FavoriteButton from '../components/sellers/FavoriteButton';
import TipButton from '../components/sellers/TipButton';
import UserActionsMenu from '../components/safety/UserActionsMenu';
import PrivateGalleryTeaser from '../components/sellers/PrivateGalleryTeaser';
import ProtectedMediaItem from '../components/media/ProtectedMediaItem';
import AlbumCard from '../components/sellers/AlbumCard';
import RatingStars from '../components/sellers/RatingStars';
import SellerReviews from '../components/sellers/SellerReviews';
import SellerSocialLinks from '../components/sellers/SellerSocialLinks';

// `redirect` is injectable because jsdom's window.location.assign is
// LegacyUnforgeable (can't be spied/stubbed) — tests pass a spy; production
// uses the default (TipModal / AlbumDetailPage pattern).
const PrincessDetailPage = ({ redirect = (url) => window.location.assign(url) }) => {
  const { princessId } = useParams();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { openSignIn } = useClerk();
  const { me, authextra, isAdmin } = useSession();
  const api = useApiClient();

  const notificationsTopic = me && `user.${me.clerk_user_id}.notifications`;

  const { data: princess, loading: princessLoading, error: princessError } = useApi(
    `/api/v1/sellers/${princessId}`
  );
  const { data: productsData, loading: productsLoading } = useApi(
    '/api/v1/products',
    { seller_id: princessId, limit: 100 }
  );
  // Public gallery (SellerMediaPublic, contract §3.7 / §4.1). The listing is
  // subscriber-aware (§4.19: active fan subscribers also receive private
  // published items), so it refetches on the notifications topic — the
  // post-checkout `fan_sub.activated` event flips the gallery live.
  const { data: mediaData, loading: mediaLoading } = useLiveQuery(
    `/api/v1/sellers/${princessId}/media`,
    { limit: 100 },
    { topics: [notificationsTopic] }
  );

  // Published priced albums (AlbumPublic, contract §3.15 / §4.16) — loads
  // progressively, so it isn't part of the page loading gate.
  const { data: albumsData } = useApi(
    `/api/v1/sellers/${princessId}/albums`,
    { limit: 100 }
  );

  // Viewer's fan subscriptions (contract §3.17 / §4.19) — drives the
  // subscribe button state and the protected-gallery rendering. One fetch,
  // signed-in only; the notifications topic keeps it live after checkout.
  // Max-size page so viewers with many subscriptions still resolve their
  // state for this seller.
  const { data: fanSubsData, refetch: refetchFanSubs } = useLiveQuery(
    '/api/v1/me/fan-subscriptions',
    { limit: 100 },
    { enabled: !!isSignedIn, topics: [notificationsTopic] }
  );

  const products = productsData?.items ?? [];
  const albums = albumsData?.items ?? [];
  const gallery = mediaData?.items ?? [];
  const privateCount = mediaData?.private_count ?? 0;
  const loading = princessLoading || productsLoading || mediaLoading;
  const error = princessError;

  // ── Fan subscription state (contract §4.19) ─────────────────────────────
  const [subSubmitting, setSubSubmitting] = useState(false);
  const [subNotice, setSubNotice] = useState(null);
  // 409 conflict on checkout means an active/past_due sub already exists —
  // treated as subscribed immediately while the list refetches.
  const [subscribedOverride, setSubscribedOverride] = useState(false);

  const fanSubs = fanSubsData?.items ?? [];
  const sellerId = princess?.id;

  // Amended §4.19 entitlement: active subs, plus canceled subs that are still
  // inside their paid period (access lasts until current_period_end).
  const isEntitling = (s) =>
    s.status === 'active' ||
    (s.status === 'canceled' &&
      s.current_period_end &&
      new Date(s.current_period_end) > new Date());

  const hasEntitlingSub =
    subscribedOverride ||
    fanSubs.some((s) => s?.seller?.id === sellerId && isEntitling(s));
  // past_due keeps the "Subscribed" button (re-subscribing 409s) but does not
  // entitle the private gallery.
  const hasCurrentSub =
    hasEntitlingSub ||
    fanSubs.some((s) => s?.seller?.id === sellerId && s.status === 'past_due');

  const isOwner =
    authextra?.user_id != null &&
    princess?.user_id != null &&
    authextra.user_id === princess.user_id;
  const fanSubEnabled = princess?.fan_sub_price_cents != null;

  // One code path for entitled viewers (§4.16/§4.19 precedent): when the
  // listing includes private items (entitled subscriber, the owner, or an
  // admin — the backend listing entitles all three), every gallery item
  // renders through the entitlement-checked file route.
  const protectGallery = hasEntitlingSub || isOwner || isAdmin;

  const handleSubscribe = async () => {
    if (!isSignedIn) {
      openSignIn();
      return;
    }
    if (subSubmitting) return;
    setSubNotice(null);
    setSubSubmitting(true);
    try {
      const { url } = await api.post(`/api/v1/sellers/${princessId}/fan-subscription-checkout`);
      redirect(url); // keep the button loading while the browser navigates away
    } catch (err) {
      if (err?.code === 'conflict') {
        setSubscribedOverride(true);
        await refetchFanSubs();
      } else if (err?.code === 'config') {
        setSubNotice("Payments aren't configured yet — please try again later.");
      } else if (err?.code === 'auth') {
        openSignIn();
      } else {
        setSubNotice(err?.message || 'Could not start the subscription checkout');
      }
      setSubSubmitting(false);
    }
  };

  // Use the first gallery image as the profile picture
  const heroImage = gallery.find((item) => item.media_type === 'image');

  // Find pedicure product (first active one, prioritise anything with "pedicure" in title)
  const pedicureProduct = products.find(p => /pedicure/i.test(p.title)) || products[0];

  const otherProducts = products;

  const handleSpoil = () => {
    navigate(`/spoil-princess/${princessId}`, {
      state: { princess, pedicureProduct }
    });
  };

  const handleMessage = () => {
    if (!isSignedIn) {
      openSignIn();
      return;
    }
    navigate(`/messages/${princess.user_id}`);
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  if (error || !princess) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
          {error?.message || 'Princess not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size="1rem" />}
          onClick={() => navigate('/princesses')}
          style={{ alignSelf: 'flex-start' }}
        >
          Back to Princesses
        </Button>

        {/* Profile Hero Card */}
        <Card shadow="md" padding="xl" radius="lg" withBorder>
          {/* Banner hero (contract §3.5 banner_image_path) */}
          {princess.banner_image_path && (
            <Card.Section mb="md">
              <img
                src={mediaUrl(princess.banner_image_path)}
                alt={`${princess.business_name} banner`}
                style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }}
              />
            </Card.Section>
          )}
          <Stack align="center" gap="md">
            {/* Large circular profile picture */}
            <Avatar
              src={heroImage ? mediaUrl(heroImage.file_path) : undefined}
              size={150}
              radius="50%"
              color="pink"
              style={{ border: '4px solid #9f38cc' }}
            >
              <IconUser size="3.5rem" />
            </Avatar>

            {/* Name + verified + favorite */}
            <Group gap="xs" justify="center">
              <Title order={2} c="grape">{princess.business_name}</Title>
              {princess.is_verified && (
                <Badge color="green" variant="light" size="md">✓ Verified</Badge>
              )}
              {/* Profile boost (contract §4.21) */}
              {princess.is_featured && (
                <Badge color="yellow" variant="filled" size="md" leftSection={<IconStar size="0.8rem" />}>
                  Featured
                </Badge>
              )}
              <FavoriteButton
                sellerId={princess.id}
                initialFavorited={princess.is_favorited}
              />
              <TipButton
                sellerId={princess.id}
                sellerUserId={princess.user_id}
                displayName={princess.display_name}
              />
              {fanSubEnabled && !isOwner && (
                hasCurrentSub ? (
                  <Button
                    size="xs"
                    variant="light"
                    color="green"
                    leftSection={<IconHeartStar size="1rem" />}
                    style={{ cursor: 'default' }}
                  >
                    Subscribed ✓
                  </Button>
                ) : (
                  <Button
                    size="xs"
                    variant="light"
                    color="grape"
                    leftSection={<IconHeartStar size="1rem" />}
                    onClick={handleSubscribe}
                    loading={subSubmitting}
                  >
                    Subscribe · {formatCents(princess.fan_sub_price_cents)}/mo
                  </Button>
                )
              )}
              <UserActionsMenu
                userId={princess.user_id}
                displayName={princess.display_name}
              />
            </Group>

            {subNotice && (
              <Alert icon={<IconExclamationCircle size="1rem" />} color="orange" variant="light">
                {subNotice}
              </Alert>
            )}

            <Text size="sm" c="dimmed">@{princess.display_name}</Text>

            {/* Social links (rendered only when set) */}
            <SellerSocialLinks seller={princess} />

            {/* Location */}
            {(princess.business_city || princess.business_state) && (
              <Group gap={4}>
                <IconMapPin size="0.9rem" color="var(--mantine-color-gray-5)" />
                <Text size="sm" c="dimmed">
                  {[princess.business_city, princess.business_state].filter(Boolean).join(', ')}
                </Text>
              </Group>
            )}

            {/* Bio */}
            <Text
              size="md"
              ta="center"
              maw={600}
              style={{ lineHeight: 1.7 }}
            >
              {princess.business_description || 'This princess hasn\'t added a bio yet.'}
            </Text>

            <Divider w="100%" my="xs" />

            {/* Stats row */}
            <Group gap="xl" justify="center">
              <Stack gap={2} align="center">
                <Text size="xs" c="dimmed">Rating</Text>
                {princess.rating_count > 0 ? (
                  <RatingStars value={princess.rating_avg} count={princess.rating_count} />
                ) : (
                  <Group gap={4}>
                    <IconStar size="0.85rem" color="var(--mantine-color-yellow-6)" />
                    <Text fw={600} size="sm">New</Text>
                  </Group>
                )}
              </Stack>
              <Stack gap={2} align="center">
                <Text size="xs" c="dimmed">Orders</Text>
                <Text fw={600} size="sm">{princess.total_orders}</Text>
              </Stack>
              <Stack gap={2} align="center">
                <Text size="xs" c="dimmed">Items for Sale</Text>
                <Text fw={600} size="sm">{products.length}</Text>
              </Stack>
              {princess.shoe_size && (
                <Stack gap={2} align="center">
                  <Text size="xs" c="dimmed">Shoe Size</Text>
                  <Text fw={600} size="sm">{princess.shoe_size}</Text>
                </Stack>
              )}
            </Group>

            <Divider w="100%" my="xs" />

            {/* CTAs */}
            <Group justify="center" gap="md">
              <Button
                leftSection={<IconMessage size="1rem" />}
                variant="light"
                color="grape"
                size="md"
                onClick={handleMessage}
              >
                Message
              </Button>
              <Button
                leftSection={<IconHeart size="1rem" />}
                size="md"
                color="pink"
                variant="gradient"
                gradient={{ from: 'pink', to: 'grape', deg: 45 }}
                onClick={handleSpoil}
              >
                Spoil this Princess 💅
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Pedicure Highlight */}
        {pedicureProduct && (
          <Card shadow="sm" padding="lg" radius="lg" withBorder
            style={{ background: 'linear-gradient(135deg, rgba(233,45,134,0.06) 0%, rgba(159,56,204,0.06) 100%)', border: '1.5px solid rgba(159,56,204,0.2)' }}
          >
            <Group justify="space-between" align="center" wrap="wrap" gap="md">
              <Stack gap={4}>
                <Group gap="xs">
                  <IconSparkles size="1.1rem" color="var(--mantine-color-grape-5)" />
                  <Text fw={700} size="lg" c="grape">Featured Pedicure</Text>
                </Group>
                <Text fw={700} size="2rem" c="pink">{formatCents(pedicureProduct.price_cents)}</Text>
                <Text size="sm" c="dimmed">{pedicureProduct.title}</Text>
              </Stack>
              <Button
                size="lg"
                color="pink"
                variant="gradient"
                gradient={{ from: 'pink', to: 'grape', deg: 45 }}
                leftSection={<IconHeart size="1.1rem" />}
                onClick={handleSpoil}
              >
                Spoil this Princess 💅
              </Button>
            </Group>
          </Card>
        )}

        {/* Public Gallery */}
        {(gallery.length > 0 || privateCount > 0) && (
          <Stack gap="md">
            <Group gap="xs">
              <ThemeIcon color="blue" variant="light" radius="xl"><IconEye size="1rem" /></ThemeIcon>
              <Title order={3}>Gallery</Title>
              <Badge color="blue" variant="light">{mediaData?.total ?? gallery.length}</Badge>
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
              {gallery.map((item) => (
                <Box key={item.id} style={{ borderRadius: 10, overflow: 'hidden', height: 150, background: '#111', position: 'relative' }}>
                  {protectGallery ? (
                    // Entitled listing includes private items; SellerMediaPublic
                    // carries no is_public flag, so every item goes through the
                    // entitlement-checked file route (one code path, §4.19).
                    <ProtectedMediaItem
                      mediaId={item.id}
                      mediaType={item.media_type}
                      title={item.title}
                      alt={item.caption || item.title || 'Gallery'}
                    />
                  ) : item.media_type === 'video'
                    ? <video src={mediaUrl(item.file_path)} controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <img src={mediaUrl(item.file_path)} alt={item.caption || item.title || 'Gallery'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  {item.media_type === 'video' && (
                    <Badge size="xs" color="blue" variant="filled" style={{ position: 'absolute', top: 6, left: 6 }}>
                      <IconVideo size="0.65rem" />
                    </Badge>
                  )}
                </Box>
              ))}
              {/* Subscribers see the private items inline — no teaser. With fan
                  subs enabled the teaser is the subscribe CTA; otherwise it
                  stays the count-only card. */}
              {!protectGallery && (
                <PrivateGalleryTeaser
                  count={privateCount}
                  priceCents={fanSubEnabled && !hasCurrentSub ? princess.fan_sub_price_cents : undefined}
                  onSubscribe={fanSubEnabled && !hasCurrentSub ? handleSubscribe : undefined}
                />
              )}
            </SimpleGrid>
          </Stack>
        )}

        {/* Priced albums (pay-per-view unlocks, contract §4.16) */}
        {albums.length > 0 && (
          <Stack gap="md">
            <Group gap="xs">
              <ThemeIcon color="grape" variant="light" radius="xl"><IconAlbum size="1rem" /></ThemeIcon>
              <Title order={3}>Albums</Title>
              <Badge color="grape" variant="light">{albumsData?.total ?? albums.length}</Badge>
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              {albums.map((album) => (
                <AlbumCard
                  key={album.id}
                  album={album}
                  onClick={() => navigate(`/albums/${album.id}`)}
                />
              ))}
            </SimpleGrid>
          </Stack>
        )}

        {/* Products */}
        {otherProducts.length > 0 ? (
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Title order={3}>
                <Group gap="xs">
                  <IconShoppingBag size="1.2rem" />
                  All Items for Sale
                  <Badge color="grape" variant="light">{productsData?.total ?? otherProducts.length}</Badge>
                </Group>
              </Title>
            </Group>

            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              {otherProducts.map((product) => {
                const imagePath = primaryImagePath(product.images);
                return (
                <Card
                  key={product.id}
                  shadow="sm"
                  padding="md"
                  radius="md"
                  withBorder
                  style={{ cursor: 'pointer', transition: 'transform 0.15s' }}
                  onClick={() => navigate(`/products/${product.id}`)}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = ''}
                >
                  {imagePath && (
                    <Card.Section>
                      <img
                        src={mediaUrl(imagePath)}
                        alt={product.title}
                        style={{ width: '100%', height: 160, objectFit: 'cover' }}
                      />
                    </Card.Section>
                  )}
                  <Stack mt="sm" gap="xs">
                    <Text fw={600} lineClamp={1}>{product.title}</Text>
                    <Text size="lg" fw={700} c="pink">{formatCents(product.price_cents)}</Text>
                    {product.id === pedicureProduct?.id && (
                      <Badge size="xs" color="grape" variant="light">Featured Pedicure</Badge>
                    )}
                  </Stack>
                </Card>
                );
              })}
            </SimpleGrid>
          </Stack>
        ) : (
          <Paper p="xl" withBorder radius="md">
            <Text ta="center" c="dimmed">No products listed yet</Text>
          </Paper>
        )}

        {/* Reviews (contract §4.10) */}
        <SellerReviews
          sellerId={princess.id}
          ratingAvg={princess.rating_avg}
          ratingCount={princess.rating_count}
        />
      </Stack>
    </Container>
  );
};

export default PrincessDetailPage;
